/**
 * Music Service
 * Handles music generation operations
 */

import { AudioPodClient } from '../client/AudioPodClient';
import {
  MusicGenerationRequest,
  MusicGenerationResult,
  Job,
  RapGenerationRequest,
  InstrumentalGenerationRequest,
  VocalsGenerationRequest,
  SamplesGenerationRequest,
  Audio2AudioRequest,
  SongBloomRequest,
  RetakeRequest,
  RepaintRequest,
  ExtendRequest,
  EditRequest
} from '../types';

export class MusicService {
  constructor(private client: AudioPodClient) {}

  /**
   * Generate music from text prompt
   */
  async generateMusic(request: MusicGenerationRequest): Promise<MusicGenerationResult> {
    const {
      prompt,
      duration = 120.0,
      guidanceScale = 7.5,
      numInferenceSteps = 50,
      seed,
      displayName,
      waitForCompletion = false,
      timeout = 600000
    } = request;

    // Validate inputs
    if (!prompt || prompt.trim().length === 0) {
      throw new Error('Music prompt cannot be empty');
    }

    if (duration !== -1 && (duration < 1.0 || duration > 600.0)) {
      throw new Error('Duration must be -1 (auto) or between 1 and 600 seconds');
    }

    if (guidanceScale < 1.0 || guidanceScale > 30.0) {
      throw new Error('Guidance scale must be between 1.0 and 30.0');
    }

    if (numInferenceSteps < 1 || numInferenceSteps > 200) {
      throw new Error('Inference steps must be between 1 and 200');
    }

    if (seed !== undefined && (seed < 0 || seed > Math.pow(2, 32) - 1)) {
      throw new Error('Seed must be between 0 and 2^32 - 1');
    }

    const data: Record<string, any> = {
      prompt: prompt.trim(),
      audio_duration: duration,
      guidance_scale: guidanceScale,
      infer_step: numInferenceSteps,
      lyrics: '',
      format: 'wav',
      scheduler_type: 'euler',
      cfg_type: 'apg',
      omega_scale: 10,
      guidance_interval: 0.5,
      guidance_interval_decay: 0.0,
      min_guidance_scale: 3.0,
      guidance_scale_text: 0.0,
      guidance_scale_lyric: 0.0,
      use_erg_tag: true,
      use_erg_lyric: true,
      use_erg_diffusion: true,
      lora_name_or_path: 'none',
      lora_weight: 1.0,
      torch_compile: false,
      cpu_offload: false,
      overlapped_decode: false,
      debug: false
    };

    if (seed !== undefined) {
      data.manual_seeds = [seed];
    }

    if (displayName) {
      data.display_name = displayName.trim();
    }

    const job = await this.client.post<Job>('/music/text2music', data);

    if (waitForCompletion) {
      const result = await this.client.waitForJobCompletion(job.id, timeout);
      return {
        job: { ...job, ...result },
        outputUrl: result.output_url || result.outputUrl,
        outputUrls: result.output_urls || result.outputUrls,
        audioDuration: result.audio_duration || result.audioDuration,
        actualSeeds: result.actual_seeds || result.actualSeeds,
        shareToken: result.share_token || result.shareToken,
        shareUrl: result.share_url || result.shareUrl,
        isShared: result.is_shared || result.isShared || false
      };
    }

    return { job };
  }

  /**
   * Generate rap music from prompt and lyrics
   */
  async generateRap(request: RapGenerationRequest): Promise<MusicGenerationResult> {
    const {
      prompt,
      lyrics,
      duration = 120.0,
      style,
      tempo,
      displayName,
      waitForCompletion = false,
      timeout = 600000
    } = request;

    // Validate inputs
    if (!prompt || prompt.trim().length === 0) {
      throw new Error('Rap prompt cannot be empty');
    }

    if (!lyrics || lyrics.trim().length === 0) {
      throw new Error('Rap lyrics cannot be empty');
    }

    if (duration !== -1 && (duration < 1.0 || duration > 600.0)) {
      throw new Error('Duration must be -1 (auto) or between 1 and 600 seconds');
    }

    const data: Record<string, any> = {
      prompt: prompt.trim(),
      lyrics: lyrics.trim(),
      audio_duration: duration,
      format: 'wav',
      // Use rap-specific LoRA by default
      lora_name_or_path: 'ACE-Step/ACE-Step-v1-chinese-rap-LoRA',
      lora_weight: 1.0
    };

    if (style) {
      data.style = style;
    }

    if (tempo) {
      data.tempo = tempo;
    }

    if (displayName) {
      data.display_name = displayName.trim();
    }

    const job = await this.client.post<Job>('/music/text2rap', data);

    if (waitForCompletion) {
      const result = await this.client.waitForJobCompletion(job.id, timeout);
      return {
        job: { ...job, ...result },
        outputUrl: result.output_url || result.outputUrl,
        outputUrls: result.output_urls || result.outputUrls,
        audioDuration: result.audio_duration || result.audioDuration,
        actualSeeds: result.actual_seeds || result.actualSeeds,
        shareToken: result.share_token || result.shareToken,
        shareUrl: result.share_url || result.shareUrl,
        isShared: result.is_shared || result.isShared || false
      };
    }

    return {
      job,
      outputUrl: undefined,
      outputUrls: undefined,
      audioDuration: undefined,
      actualSeeds: undefined,
      shareToken: undefined,
      shareUrl: undefined,
      isShared: false
    };
  }

  /**
   * Generate instrumental music from prompt
   */
  async generateInstrumental(request: InstrumentalGenerationRequest): Promise<MusicGenerationResult> {
    const {
      prompt,
      duration = 120.0,
      instruments,
      key,
      tempo,
      displayName,
      waitForCompletion = false,
      timeout = 600000
    } = request;

    // Validate inputs
    if (!prompt || prompt.trim().length === 0) {
      throw new Error('Instrumental prompt cannot be empty');
    }

    if (duration !== -1 && (duration < 1.0 || duration > 600.0)) {
      throw new Error('Duration must be -1 (auto) or between 1 and 600 seconds');
    }

    const data: Record<string, any> = {
      prompt: prompt.trim(),
      audio_duration: duration,
      format: 'wav',
      lyrics: '' // Empty for instrumental
    };

    if (instruments) {
      data.instruments = instruments;
    }

    if (key) {
      data.key = key;
    }

    if (tempo) {
      data.tempo = tempo;
    }

    if (displayName) {
      data.display_name = displayName.trim();
    }

    const job = await this.client.post<Job>('/music/prompt2instrumental', data);

    if (waitForCompletion) {
      const result = await this.client.waitForJobCompletion(job.id, timeout);
      return {
        job: { ...job, ...result },
        outputUrl: result.output_url || result.outputUrl,
        outputUrls: result.output_urls || result.outputUrls,
        audioDuration: result.audio_duration || result.audioDuration,
        actualSeeds: result.actual_seeds || result.actualSeeds,
        shareToken: result.share_token || result.shareToken,
        shareUrl: result.share_url || result.shareUrl,
        isShared: result.is_shared || result.isShared || false
      };
    }

    return {
      job,
      outputUrl: undefined,
      outputUrls: undefined,
      audioDuration: undefined,
      actualSeeds: undefined,
      shareToken: undefined,
      shareUrl: undefined,
      isShared: false
    };
  }

  /**
   * Generate vocals from prompt and lyrics
   */
  async generateVocals(request: VocalsGenerationRequest): Promise<MusicGenerationResult> {
    const {
      prompt,
      lyrics,
      duration = 120.0,
      displayName,
      waitForCompletion = false,
      timeout = 600000
    } = request;

    // Validate inputs
    if (!prompt || prompt.trim().length === 0) {
      throw new Error('Vocals prompt cannot be empty');
    }

    if (!lyrics || lyrics.trim().length === 0) {
      throw new Error('Vocals lyrics cannot be empty');
    }

    if (duration !== -1 && (duration < 1.0 || duration > 600.0)) {
      throw new Error('Duration must be -1 (auto) or between 1 and 600 seconds');
    }

    const data: Record<string, any> = {
      prompt: prompt.trim(),
      lyrics: lyrics.trim(),
      audio_duration: duration,
      format: 'wav'
    };

    if (displayName) {
      data.display_name = displayName.trim();
    }

    const job = await this.client.post<Job>('/music/lyric2vocals', data);

    if (waitForCompletion) {
      const result = await this.client.waitForJobCompletion(job.id, timeout);
      return {
        job: { ...job, ...result },
        outputUrl: result.output_url || result.outputUrl,
        outputUrls: result.output_urls || result.outputUrls,
        audioDuration: result.audio_duration || result.audioDuration,
        actualSeeds: result.actual_seeds || result.actualSeeds,
        shareToken: result.share_token || result.shareToken,
        shareUrl: result.share_url || result.shareUrl,
        isShared: result.is_shared || result.isShared || false
      };
    }

    return {
      job,
      outputUrl: undefined,
      outputUrls: undefined,
      audioDuration: undefined,
      actualSeeds: undefined,
      shareToken: undefined,
      shareUrl: undefined,
      isShared: false
    };
  }

  /**
   * Generate audio samples from prompt
   */
  async generateSamples(request: SamplesGenerationRequest): Promise<MusicGenerationResult> {
    const {
      prompt,
      sampleType = 'loop',
      tempo = 128,
      duration = 8.0,
      displayName,
      waitForCompletion = false,
      timeout = 600000
    } = request;

    // Validate inputs
    if (!prompt || prompt.trim().length === 0) {
      throw new Error('Samples prompt cannot be empty');
    }

    if (duration !== -1 && (duration < 1.0 || duration > 120.0)) {
      throw new Error('Duration must be -1 (auto) or between 1 and 120 seconds for samples');
    }

    const data: Record<string, any> = {
      prompt: prompt.trim(),
      sample_type: sampleType,
      tempo: tempo,
      audio_duration: duration,
      format: 'wav'
    };

    if (displayName) {
      data.display_name = displayName.trim();
    }

    const job = await this.client.post<Job>('/music/text2samples', data);

    if (waitForCompletion) {
      const result = await this.client.waitForJobCompletion(job.id, timeout);
      return {
        job: { ...job, ...result },
        outputUrl: result.output_url || result.outputUrl,
        outputUrls: result.output_urls || result.outputUrls,
        audioDuration: result.audio_duration || result.audioDuration,
        actualSeeds: result.actual_seeds || result.actualSeeds,
        shareToken: result.share_token || result.shareToken,
        shareUrl: result.share_url || result.shareUrl,
        isShared: result.is_shared || result.isShared || false
      };
    }

    return {
      job,
      outputUrl: undefined,
      outputUrls: undefined,
      audioDuration: undefined,
      actualSeeds: undefined,
      shareToken: undefined,
      shareUrl: undefined,
      isShared: false
    };
  }

  /**
   * Transform audio to audio with new characteristics
   */
  async generateAudio2Audio(request: Audio2AudioRequest): Promise<MusicGenerationResult> {
    const {
      sourceFile,
      prompt,
      refAudioStrength = 0.7,
      audioDuration = 120.0,
      inferStep = 60,
      guidanceScale = 15.0,
      displayName,
      waitForCompletion = false,
      timeout = 600000
    } = request;

    // Validate inputs
    if (!prompt || prompt.trim().length === 0) {
      throw new Error('Audio2audio prompt cannot be empty');
    }

    let fileData: File | Blob;

    if (typeof sourceFile === 'string') {
      const fs = await import('fs');
      const path = await import('path');

      if (!fs.existsSync(sourceFile)) {
        throw new Error(`Source audio file not found: ${sourceFile}`);
      }

      const fileBuffer = fs.readFileSync(sourceFile);
      const fileName = path.basename(sourceFile);
      fileData = new Blob([fileBuffer], { type: 'audio/*' });
      (fileData as any).name = fileName;
    } else {
      fileData = sourceFile;
    }

    const formData: Record<string, any> = {
      prompt: prompt.trim(),
      ref_audio_strength: refAudioStrength,
      audio_duration: audioDuration,
      infer_step: inferStep,
      guidance_scale: guidanceScale
    };

    if (displayName) {
      formData.display_name = displayName.trim();
    }

    const job = await this.client.uploadFile<Job>(
      '/music/audio2audio',
      fileData,
      formData
    );

    if (waitForCompletion) {
      const result = await this.client.waitForJobCompletion(job.id, timeout);
      return {
        job: { ...job, ...result },
        outputUrl: result.output_url || result.outputUrl,
        outputUrls: result.output_urls || result.outputUrls,
        audioDuration: result.audio_duration || result.audioDuration,
        actualSeeds: result.actual_seeds || result.actualSeeds,
        shareToken: result.share_token || result.shareToken,
        shareUrl: result.share_url || result.shareUrl,
        isShared: result.is_shared || result.isShared || false
      };
    }

    return {
      job,
      outputUrl: undefined,
      outputUrls: undefined,
      audioDuration: undefined,
      actualSeeds: undefined,
      shareToken: undefined,
      shareUrl: undefined,
      isShared: false
    };
  }

  /**
   * Generate music using SongBloom with reference audio
   */
  async generateSongBloom(request: SongBloomRequest): Promise<MusicGenerationResult> {
    const {
      lyrics,
      referenceFile,
      duration = 120.0,
      guidanceScale = 7.5,
      numInferenceSteps = 50,
      seed,
      displayName,
      waitForCompletion = false,
      timeout = 600000
    } = request;

    // Validate inputs
    if (!lyrics || lyrics.trim().length === 0) {
      throw new Error('SongBloom lyrics cannot be empty');
    }

    if (duration !== -1 && (duration < 10.0 || duration > 600.0)) {
      throw new Error('Duration must be -1 (auto) or between 10 and 600 seconds for SongBloom');
    }

    let fileData: File | Blob;

    if (typeof referenceFile === 'string') {
      const fs = await import('fs');
      const path = await import('path');

      if (!fs.existsSync(referenceFile)) {
        throw new Error(`Reference audio file not found: ${referenceFile}`);
      }

      const fileBuffer = fs.readFileSync(referenceFile);
      const fileName = path.basename(referenceFile);
      fileData = new Blob([fileBuffer], { type: 'audio/*' });
      (fileData as any).name = fileName;
    } else {
      fileData = referenceFile;
    }

    const formData: Record<string, any> = {
      lyrics: lyrics.trim(),
      duration: duration,
      guidance_scale: guidanceScale,
      num_inference_steps: numInferenceSteps
    };

    if (seed !== undefined) {
      formData.seed = seed;
    }

    if (displayName) {
      formData.display_name = displayName.trim();
    }

    const job = await this.client.uploadFile<Job>(
      '/music/songbloom',
      fileData,
      formData
    );

    if (waitForCompletion) {
      const result = await this.client.waitForJobCompletion(job.id, timeout);
      return {
        job: { ...job, ...result },
        outputUrl: result.output_url || result.outputUrl,
        outputUrls: result.output_urls || result.outputUrls,
        audioDuration: result.audio_duration || result.audioDuration,
        actualSeeds: result.actual_seeds || result.actualSeeds,
        shareToken: result.share_token || result.shareToken,
        shareUrl: result.share_url || result.shareUrl,
        isShared: result.is_shared || result.isShared || false
      };
    }

    return {
      job,
      outputUrl: undefined,
      outputUrls: undefined,
      audioDuration: undefined,
      actualSeeds: undefined,
      shareToken: undefined,
      shareUrl: undefined,
      isShared: false
    };
  }

  /**
   * Create a retake/variation of existing music
   */
  async retakeMusic(request: RetakeRequest): Promise<MusicGenerationResult> {
    const {
      originalJobId,
      retakeVariance = 0.5,
      retakeSeeds,
      displayName,
      waitForCompletion = false,
      timeout = 600000
    } = request;

    const data: Record<string, any> = {
      original_job_id: originalJobId,
      retake_variance: retakeVariance
    };

    if (retakeSeeds) {
      data.retake_seeds = retakeSeeds;
    }

    if (displayName) {
      data.display_name = displayName.trim();
    }

    const job = await this.client.post<Job>('/music/retake', data);

    if (waitForCompletion) {
      const result = await this.client.waitForJobCompletion(job.id, timeout);
      return {
        job: { ...job, ...result },
        outputUrl: result.output_url || result.outputUrl,
        outputUrls: result.output_urls || result.outputUrls,
        audioDuration: result.audio_duration || result.audioDuration,
        actualSeeds: result.actual_seeds || result.actualSeeds,
        shareToken: result.share_token || result.shareToken,
        shareUrl: result.share_url || result.shareUrl,
        isShared: result.is_shared || result.isShared || false
      };
    }

    return {
      job,
      outputUrl: undefined,
      outputUrls: undefined,
      audioDuration: undefined,
      actualSeeds: undefined,
      shareToken: undefined,
      shareUrl: undefined,
      isShared: false
    };
  }

  /**
   * Repaint sections of existing music
   */
  async repaintMusic(request: RepaintRequest): Promise<MusicGenerationResult> {
    const {
      sourceJobId,
      repaintStart,
      repaintEnd,
      prompt,
      lyrics = '',
      displayName,
      waitForCompletion = false,
      timeout = 600000
    } = request;

    // Validate inputs
    if (!prompt || prompt.trim().length === 0) {
      throw new Error('Repaint prompt cannot be empty');
    }

    if (repaintEnd <= repaintStart) {
      throw new Error('Repaint end time must be greater than start time');
    }

    const data: Record<string, any> = {
      source_job_id: sourceJobId,
      repaint_start: repaintStart,
      repaint_end: repaintEnd,
      prompt: prompt.trim(),
      lyrics: lyrics.trim()
    };

    if (displayName) {
      data.display_name = displayName.trim();
    }

    const job = await this.client.post<Job>('/music/repaint', data);

    if (waitForCompletion) {
      const result = await this.client.waitForJobCompletion(job.id, timeout);
      return {
        job: { ...job, ...result },
        outputUrl: result.output_url || result.outputUrl,
        outputUrls: result.output_urls || result.outputUrls,
        audioDuration: result.audio_duration || result.audioDuration,
        actualSeeds: result.actual_seeds || result.actualSeeds,
        shareToken: result.share_token || result.shareToken,
        shareUrl: result.share_url || result.shareUrl,
        isShared: result.is_shared || result.isShared || false
      };
    }

    return {
      job,
      outputUrl: undefined,
      outputUrls: undefined,
      audioDuration: undefined,
      actualSeeds: undefined,
      shareToken: undefined,
      shareUrl: undefined,
      isShared: false
    };
  }

  /**
   * Extend existing music with additional content
   */
  async extendMusic(request: ExtendRequest): Promise<MusicGenerationResult> {
    const {
      sourceJobId,
      leftExtendLength = 0.0,
      rightExtendLength = 30.0,
      prompt,
      lyrics = '',
      extendSeeds,
      displayName,
      waitForCompletion = false,
      timeout = 600000
    } = request;

    // Validate inputs
    if (!prompt || prompt.trim().length === 0) {
      throw new Error('Extend prompt cannot be empty');
    }

    if (leftExtendLength < 0 || leftExtendLength > 60) {
      throw new Error('Left extend length must be between 0 and 60 seconds');
    }

    if (rightExtendLength < 0 || rightExtendLength > 60) {
      throw new Error('Right extend length must be between 0 and 60 seconds');
    }

    const data: Record<string, any> = {
      source_job_id: sourceJobId,
      left_extend_length: leftExtendLength,
      right_extend_length: rightExtendLength,
      prompt: prompt.trim(),
      lyrics: lyrics.trim()
    };

    if (extendSeeds) {
      data.extend_seeds = extendSeeds;
    }

    if (displayName) {
      data.display_name = displayName.trim();
    }

    const job = await this.client.post<Job>('/music/extend', data);

    if (waitForCompletion) {
      const result = await this.client.waitForJobCompletion(job.id, timeout);
      return {
        job: { ...job, ...result },
        outputUrl: result.output_url || result.outputUrl,
        outputUrls: result.output_urls || result.outputUrls,
        audioDuration: result.audio_duration || result.audioDuration,
        actualSeeds: result.actual_seeds || result.actualSeeds,
        shareToken: result.share_token || result.shareToken,
        shareUrl: result.share_url || result.shareUrl,
        isShared: result.is_shared || result.isShared || false
      };
    }

    return {
      job,
      outputUrl: undefined,
      outputUrls: undefined,
      audioDuration: undefined,
      actualSeeds: undefined,
      shareToken: undefined,
      shareUrl: undefined,
      isShared: false
    };
  }

  /**
   * Edit existing music with new style or lyrics
   */
  async editMusic(request: EditRequest): Promise<MusicGenerationResult> {
    const {
      sourceJobId,
      editTargetPrompt,
      editTargetLyrics = '',
      editType = 'remix',
      editNMin = 0.0,
      editNMax = 1.0,
      editNAvg = 1,
      displayName,
      waitForCompletion = false,
      timeout = 600000
    } = request;

    // Validate inputs
    if (!editTargetPrompt || editTargetPrompt.trim().length === 0) {
      throw new Error('Edit target prompt cannot be empty');
    }

    if (!['remix', 'only_lyrics'].includes(editType)) {
      throw new Error('Edit type must be "remix" or "only_lyrics"');
    }

    const data: Record<string, any> = {
      source_job_id: sourceJobId,
      edit_target_prompt: editTargetPrompt.trim(),
      edit_target_lyrics: editTargetLyrics.trim(),
      edit_type: editType,
      edit_n_min: editNMin,
      edit_n_max: editNMax,
      edit_n_avg: editNAvg
    };

    if (displayName) {
      data.display_name = displayName.trim();
    }

    const job = await this.client.post<Job>('/music/edit', data);

    if (waitForCompletion) {
      const result = await this.client.waitForJobCompletion(job.id, timeout);
      return {
        job: { ...job, ...result },
        outputUrl: result.output_url || result.outputUrl,
        outputUrls: result.output_urls || result.outputUrls,
        audioDuration: result.audio_duration || result.audioDuration,
        actualSeeds: result.actual_seeds || result.actualSeeds,
        shareToken: result.share_token || result.shareToken,
        shareUrl: result.share_url || result.shareUrl,
        isShared: result.is_shared || result.isShared || false
      };
    }

    return {
      job,
      outputUrl: undefined,
      outputUrls: undefined,
      audioDuration: undefined,
      actualSeeds: undefined,
      shareToken: undefined,
      shareUrl: undefined,
      isShared: false
    };
  }

  /**
   * Get music generation job status
   */
  async getJobStatus(jobId: number): Promise<Job> {
    return this.client.get<Job>(`/music/jobs/${jobId}/status`);
  }

  /**
   * List music generation jobs
   */
  async listJobs(options: {
    limit?: number;
    offset?: number;
    status?: string;
  } = {}): Promise<Job[]> {
    const { limit = 50, offset = 0, status } = options;

    const params: Record<string, any> = {
      limit,
      offset
    };

    if (status) {
      params.status = status;
    }

    return this.client.get<Job[]>('/music/jobs', params);
  }

  /**
   * Like a music track
   */
  async likeMusicTrack(jobId: number): Promise<{ liked: boolean; totalLikes: number }> {
    const result = await this.client.post<any>(`/music/jobs/${jobId}/like`);
    return {
      liked: result.liked,
      totalLikes: result.total_likes || result.totalLikes
    };
  }

  /**
   * Unlike a music track
   */
  async unlikeMusicTrack(jobId: number): Promise<{ liked: boolean; totalLikes: number }> {
    const result = await this.client.delete<any>(`/music/jobs/${jobId}/like`);
    return {
      liked: result.liked,
      totalLikes: result.total_likes || result.totalLikes
    };
  }

  /**
   * Share a music track
   */
  async shareMusicTrack(jobId: number, options: {
    title?: string;
    description?: string;
    expirationDate?: string;
  } = {}): Promise<{ shareToken: string; shareUrl: string }> {
    const result = await this.client.post<any>(`/music/jobs/${jobId}/share`, options);
    return {
      shareToken: result.share_token || result.shareToken,
      shareUrl: result.share_url || result.shareUrl
    };
  }

  /**
   * Get music track statistics
   */
  async getMusicTrackStats(jobId: number): Promise<{
    likes: number;
    dislikes: number;
    shares: number;
    comments: number;
  }> {
    return this.client.get(`/music/jobs/${jobId}/stats`);
  }

  /**
   * Delete a music generation job
   */
  async deleteMusicJob(jobId: number): Promise<void> {
    await this.client.delete(`/music/jobs/${jobId}`);
  }

  /**
   * Get shared music track (public access)
   */
  async getSharedTrack(shareToken: string): Promise<MusicGenerationResult> {
    const job = await this.client.get<any>(`/music/share/${shareToken}`);

    return {
      job,
      outputUrl: job.output_url || job.outputUrl,
      outputUrls: job.output_urls || job.outputUrls,
      audioDuration: job.audio_duration || job.audioDuration,
      isShared: true,
      shareToken,
      shareUrl: job.share_url || job.shareUrl
    };
  }
}